import React, { useState } from 'react';
import { message, Spin } from 'antd';
import { CourseArchiveIcon, LockIcon, PlayIcon, RefreshIcon, TestStartIcon } from 'icons';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { useSaveTest } from 'modules/saved-tests';

import { Visible } from 'components';

interface IProps {
  year: number;
  rating: number;
  testId: number;
  language: string;
  isSaved: boolean;
  testIndex: number;
  isPremium: boolean;
  score: number | null;
  onStart: (testId: number) => void;
  isStartingTest?: boolean;
}

export const Card = ({
  testIndex,
  language,
  year,
  score,
  rating,
  testId,
  isSaved,
  isPremium,
  onStart,
  isStartingTest = false
}: IProps) => {
  const t = useTranslations();
  const saveTest = useSaveTest();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user);
  const isAuth = useAppSelector(({ user }) => user?.phone);
  const [isSavedState, setIsSavedState] = useState<boolean>(isSaved);

  const hasScore = score !== null && score !== undefined;

  const onSaveTest = (newStatus: boolean) => {
    if (testId == null) return;

    if (user.phone) {
      saveTest.mutate(
        { id: testId, status: newStatus },
        {
          onSuccess: () => {
            setIsSavedState(newStatus);

            message.success(t('Muvaffaqiyatli_bajarildi'));
          }
        }
      );
    } else {
      dispatch(openAuthModal());
    }
  };

  const onStartTest = () => {
    if (!isAuth) {
      return dispatch(openAuthModal());
    }

    return onStart(testId);
  };

  return (
    <div className="relative">
      {isPremium && (
        <Link
          href="/pro"
          className="absolute inset-0 z-10 flex cursor-pointer flex-col items-center justify-center rounded-2xl bg-white/70 backdrop-blur-[4px] transition hover:bg-white/80"
        >
          <LockIcon />
          <span className="text-xs text-gray-400">{t('akam_pro_ga_obuna_boling')}</span>
        </Link>
      )}
      <div className="shadow-gray-sm hover:shadow-green-sm relative rounded-2xl border border-gray-300 bg-white px-4 pb-4 transition-all duration-300 hover:border-green-500">
        {hasScore ? (
          <div className="absolute left-3 top-3 flex h-6 items-center justify-center rounded-full bg-green-100 px-1.5 text-xs text-gray-800">
            {t('ball')}: {score}
          </div>
        ) : null}
        <div
          onClick={e => {
            e.stopPropagation();
            onSaveTest(!isSavedState);
          }}
          className="absolute right-4 top-4 cursor-pointer"
        >
          <Visible
            if={saveTest.isLoading}
            else={
              <Visible
                if={isSavedState}
                else={
                  <span className="text-gray-500">
                    <CourseArchiveIcon className="size-5" />
                  </span>
                }
              >
                <span className="text-green-500">
                  <CourseArchiveIcon className="size-5" />
                </span>
              </Visible>
            }
          >
            <Spin size="small" />
          </Visible>
        </div>
        <div className="mt-9.5 flex flex-col items-center justify-center space-y-4">
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center justify-center rounded-lg bg-gray-100 p-1.5">
              <div className="text-5xl font-bold text-gray-700">{String(testIndex).padStart(2, '0')}</div>
            </div>
          </div>
          <p className="text-center text-lg font-semibold capitalize">{t('test')}</p>
          <div className="flex items-center gap-1.5">
            <div className="flex items-center justify-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              {year}
            </div>
            <div className="flex items-center justify-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              {language}
            </div>
            {rating > 0 ? (
              <div className="flex items-center justify-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                <TestStartIcon className="size-3.5" />
                {rating}
              </div>
            ) : null}
          </div>
        </div>
        <Spin spinning={isStartingTest}>
          {hasScore ? (
            <button
              onClick={onStartTest}
              className="mt-4 flex h-9 w-full cursor-pointer items-center justify-center gap-1 rounded-xl bg-gray-100 text-sm font-medium text-gray-800"
            >
              <RefreshIcon /> {t('qayta_ishlash')}
            </button>
          ) : (
            <button
              onClick={onStartTest}
              className="mt-4 flex h-9 w-full cursor-pointer items-center justify-center gap-1 rounded-xl bg-green-500 text-sm font-medium text-white transition-colors duration-200 hover:bg-green-600"
            >
              {t('boshlash')} <PlayIcon />
            </button>
          )}
        </Spin>
      </div>
    </div>
  );
};
